/** @jsx jsx */
/* eslint-disable jsx-a11y/control-has-associated-label, no-nested-ternary */
import React from 'react';
import { css, jsx } from '@emotion/core';
import { BoardData, PrintSetting, ShiftItem } from 'domain/attend/shiftBoard/types';
import {
 defaultTimeFormatter, DOW_LABELS, parseDateStr, TimeFormatter,
} from 'domain/attend/shiftBoard/time';
import {
  defaultPatternColor, fontSizePx, hasItem, nameIsTime, textColorFor,
} from 'domain/attend/shiftBoard/print';
import { printColumnWidthMm } from 'domain/attend/shiftBoard/layout';
import { holidayLook, LABOR_COST_TIP, laborOnly } from './styles';

const styles = {
  sheet: css({
    background: '#fff',
    color: '#111',
    fontFamily: '"Hiragino Sans", "Yu Gothic", Meiryo, sans-serif',
    '@media print': {
      margin: 0,
    },
  }),
  header: css({
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    marginBottom: '6px',
  }),
  table: css({
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
    '& th, & td': {
      border: '1px solid var(--print-border, #999)',
      padding: '2px 3px',
      verticalAlign: 'top',
      // 文字を切らずに折り返す（9/16 決定）。行の高さは内容に合わせて伸ばす
      overflowWrap: 'anywhere',
      wordBreak: 'normal',
    },
    '& th': { background: '#F0F0F0', textAlign: 'center' },
    // 日付の列は、入りきらない時刻をセルの中で切る（隣の列に重ねない。10/5）。時刻以外の文字はセルの中で折り返す
    '& th[data-day], & td[data-day]': { overflow: 'hidden' },
    // 日付の見出し・人数の行は、数字を 1 桁ずつ折らない（曜日・店休日の行は空白、人数の行は「/」のあとで折り返す）
    '& th[data-day]': { overflowWrap: 'normal' },
    // 時刻を 2 行に分けるとき（列が狭いとき）は、日付の列の左右の余白を詰める
    '&[data-narrow="true"] th[data-day], &[data-narrow="true"] td[data-day]': { paddingLeft: '1px', paddingRight: '1px' },
    '&[data-thick="true"] th, &[data-thick="true"] td': { borderWidth: '1.2px' },
    '& tbody tr[data-stripe="true"] td': { background: 'var(--print-stripe, transparent)' },
  }),
  legend: css({
    marginTop: '8px',
    paddingTop: '6px',
    borderTop: '1px solid var(--print-border, #999)',
    // 用紙の幅に応じて列数が決まる。1 件が途中で折り返さないよう桁を揃える
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
    gap: '3px 12px',
    lineHeight: 1.4,
    pageBreakInside: 'avoid',
  }),
  legendItem: css({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
    breakInside: 'avoid',
  }),
  legendSwatch: css({
    // 短縮名の長さに合わせて伸びる。折り返さない
    display: 'inline-block',
    minWidth: '22px',
    padding: '0 3px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    border: '1px solid var(--print-border, #999)',
    borderRadius: '2px',
    fontWeight: 700,
    flex: 'none',
  }),
  legendText: css({
    overflowWrap: 'anywhere',
    whiteSpace: 'normal',
  }),
  // 紙は横にスクロールできないので、はみ出す文字は省略せずに折り返す（9/16 決定）
  chip: css({
    display: 'block',
    padding: '1px 3px',
    borderRadius: '3px',
    marginBottom: '1px',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: 1.3,
    textAlign: 'center',
  }),
  // 1ヶ月表示は 1 列が 8mm 前後しかなく、開始と終了が 1 行に収まらない。
  // 省略せずに読めるよう行を分ける
  chipNarrow: css({
    display: 'block',
    // 左右の余白は 0（列が狭いときに時刻を入れるため。10/5）
    padding: '1px 0',
    borderRadius: '3px',
    marginBottom: '1px',
    lineHeight: 1.25,
    textAlign: 'center',
    whiteSpace: 'normal',
    wordBreak: 'keep-all',
  }),
  // 時刻の行。1 行で出し、入りきらない分はセルの中で切れる
  chipLine: css({
    display: 'block',
    whiteSpace: 'nowrap',
  }),
  // 時刻以外の行（パターン略称・公休・休憩・支援先・業務）。セルの中で折り返す（9/16 の「省略せず折り返す」。10/5）
  chipWrap: css({
    display: 'block',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  }),
};

/**
 * 凡例の説明文。パターン名が時間そのものの企業があるため、時間が名前に含まれるときは足さない
 *
 * 名前は「9:00-18:00」「９：００－１８：００」のように、ゼロ埋めなし・全角で書かれていることがある。
 * 全角を半角にし、時の先頭の 0 を落としてから数字だけで比べる（9/24。以前は同じ時間が 2 回出ていた）
 */
const legendLabel = (p: { name: string; start: string | null; end: string | null }): { name: string; time: string } => {
  const time = p.start && p.end ? `${p.start}-${p.end}` : '';
  const name = (p.name || '').trim();
  if (!time) {
    return { name, time: '' };
  }
  if (!name || nameIsTime(name, p.start, p.end)) {
    return { name: '', time };
  }
  return { name, time };
};

/** 印刷の土日の文字色（9/16 決定）。シフト表の mock.primary / mock.secondary に合わせる */
const PRINT_SAT = '#0D4796';
const PRINT_SUN = '#D1506E';

/**
 * 月をまたぐ期間で「2026年9月」「2026年10月」の見出しを作る。またがないときは空
 */
const monthSpans = (dates: Array<string>): Array<{ label: string; span: number }> => {
  const out: Array<{ label: string; span: number }> = [];
  dates.forEach((date) => {
    const d = parseDateStr(date);
    const label = `${d.getFullYear()}年${d.getMonth() + 1}月`;
    const last = out[out.length - 1];
    if (last && last.label === label) {
      last.span += 1;
    } else {
      out.push({ label, span: 1 });
    }
  });
  return out.length > 1 ? out : [];
};

export type PrintSheetProps = {
  board: BoardData;
  setting: PrintSetting;
  dateFrom: string;
  dateTo: string;
  /** 時間量の整形。60 進/10 進とその丸めは労務規約項目マスタに合わせる（9/23） */
  formatTime?: TimeFormatter;
};

/**
 * 紙に貼る前提の印刷本体（A4/A3 横）
 */
const PrintSheet: React.FC<PrintSheetProps> = ({
  board, setting, dateFrom, dateTo, formatTime = defaultTimeFormatter,
}) => {
  const fs = fontSizePx(setting.fontSize);
  const colMm = setting.fitToWidth
    ? printColumnWidthMm(board.days.length, setting.paper, 34, 18, 10, setting.orientation)
    : 14;
  /*
    セルの文字の配分（9/24 に組み直し）
    - 時刻が主役。列の幅に収まる範囲で、本文の 1.3 倍を上限に大きく太く出す。途中で折り返さない
    - 「08:45-17:45」が 1 行に本文以上の大きさで入らない列は、開始と終了を 2 行に分ける
    - パターン略称は目印なので、時刻の上に本文より小さく添える
    幅の見積もり（Chromium・ヒラギノ角ゴシック太字で実測。10/5）: 「08:45-17:45」6.94em、「08:45」3.25em、「17:45翌」4.25em
    以前は游ゴシックの値（5.63em・2.60em・3.60em）で、iPad・Mac では 25% ほどはみ出していた。游ゴシック・メイリオは幅が狭いので、こちらでも収まる
    - 2 行に分けるときは、日付の列の左右の余白を詰め（セル 1px・カード 0）、時刻の下限を 8px にする（1ヶ月・A4 横で「07:00」が入る）
    - それでも入らない長い期間では、時刻はセルの中で切れる（改ページはしない。10/5 決定）
  */
  const cellPx = colMm * 3.78 - 12; // td と chip の左右の余白を引いた幅（1 行に収まるかの判定）
  const nextDay = board.shifts.some((s) => s.isEndNextDay);
  const maxTimeFs = Math.round(fs * 1.3);
  const oneLineFs = Math.floor(cellPx / (nextDay ? 8.0 : 7.0));
  const narrow = oneLineFs < fs;
  // 2 行のときに使える幅（td 1px・カード 0。モノクロはカードの枠 1px も引く）
  const narrowPx = colMm * 3.78 - (setting.monochrome ? 6 : 4);
  const timeFs = narrow
    ? Math.max(8, Math.min(maxTimeFs, Math.floor(narrowPx / (nextDay ? 4.3 : 3.3))))
    : Math.max(9, Math.min(maxTimeFs, oneLineFs));
  // 日付の数字・人数の行は、2 桁が列に入る大きさまで小さくする（下限 6px。切ると「31」が「3」に見えるため、切らない）
  const dateFs = Math.max(6, Math.min(fs, Math.floor((narrow ? narrowPx : colMm * 3.78 - 8) / 1.3)));
  const labelFs = Math.max(8, fs - 2);
  const byCell: { [key: string]: Array<ShiftItem> } = {};
  board.shifts.forEach((s) => {
    const k = `${s.staffCode}|${s.targetDate}`;
    byCell[k] = byCell[k] || [];
    byCell[k].push(s);
  });
  const hopes: { [key: string]: string } = {};
  if (hasItem(setting, 'hope')) {
    board.hopeShifts.forEach((h) => {
      hopes[`${h.staffCode}|${h.targetDate}`] = h.type === 2 ? '休希望' : `希${h.start || ''}`;
    });
  }
  const required: { [date: string]: { required: number | null; assigned: number } } = {};
  board.requiredStaff.forEach((r) => {
    required[r.date] = r;
  });
  type StaffTotal = { workDays: number; workMinutes: number; laborCost: number | null; transportation: number | null };
  const byStaff: { [code: string]: StaffTotal } = {};
  board.summary.byStaff.forEach((s) => {
    byStaff[s.staffCode] = s;
  });
  const showTotals = hasItem(setting, 'totals');
  const showLabor = hasItem(setting, 'laborCost') && board.role.canViewLaborCostFulltime && board.role.canViewLaborCostPart;
  const mono = setting.monochrome;

  // 業務ごとにページを分けるときの区切り。スタッフの主業務で分ける
  const businessName: { [id: string]: string } = {};
  board.businesses.forEach((b) => {
    businessName[b.businessId] = b.businessName;
  });
  const groups: Array<{ key: string; label: string; staffs: typeof board.staffs }> = [];
  if (setting.splitByBusiness) {
    board.staffs.forEach((st) => {
      const key = st.mainBusinessId || '';
      const label = key ? (businessName[key] || key) : '業務未設定';
      const found = groups.filter((g) => g.key === key)[0];
      if (found) {
        found.staffs.push(st);
      } else {
        groups.push({ key, label, staffs: [st] });
      }
    });
  } else {
    groups.push({ key: '', label: '', staffs: board.staffs });
  }

  // 月をまたぐ期間は日付の上に月の行を足す（9/16 決定）
  const months = monthSpans(board.days.map((d) => d.date));

  const f = parseDateStr(dateFrom);
  const t = parseDateStr(dateTo);
  const title = `${f.getFullYear()}年${f.getMonth() + 1}月${f.getDate()}日 〜 ${t.getMonth() + 1}月${t.getDate()}日`;

  return (
    <div
      css={styles.sheet}
      style={{
        fontSize: `${fs}px`,
        ['--print-border' as any]: setting.thickBorder ? setting.borderColor : '#999',
        ['--print-stripe' as any]: mono || !setting.stripeColor ? 'transparent' : setting.stripeColor,
      }}
      className="shift-print-sheet"
    >
      {setting.header && (
        <div css={styles.header}>
          <span style={{ fontSize: `${Math.round((fs + 6) * (setting.headerScale / 100))}px`, fontWeight: 700 }}>{board.org.orgName}</span>
          <span style={{ fontSize: `${Math.round((fs + 2) * (setting.headerScale / 100))}px` }}>{`シフト表 ${title}`}</span>
          {board.period.confirmStatus === 'CONFIRMED' && (
            <span style={{ marginLeft: 'auto', fontSize: `${fs - 2}px` }}>確定</span>
          )}
        </div>
      )}
      {groups.map((g, gi) => (
        <div key={g.key || 'all'} style={{ pageBreakAfter: gi < groups.length - 1 ? 'always' : 'auto' }}>
          {setting.splitByBusiness && (
            <div style={{ fontWeight: 700, margin: '6px 0 4px', fontSize: `${Math.round((fs + 2) * (setting.headerScale / 100))}px` }}>{g.label}</div>
          )}
          <table css={styles.table} data-thick={setting.thickBorder} data-narrow={narrow}>
            <colgroup>
              <col style={{ width: '34mm' }} />
              {board.days.map((d) => <col key={d.date} style={{ width: `${colMm}mm` }} />)}
              {showTotals && <col style={{ width: '18mm' }} />}
            </colgroup>
            <thead>
              {months.length > 0 && (
              <tr>
                <th />
                {months.map((m) => <th key={m.label} colSpan={m.span}>{m.label}</th>)}
                {showTotals && <th />}
              </tr>
            )}
              <tr>
                <th>スタッフ</th>
                {board.days.map((d) => {
              const { dow } = d;
              // 土は青、日と祝日は赤（9/16 決定）。モノクロ印刷では色を付けない
              const isSun = dow === 0 || !!d.publicHolidayName;
              const fg = mono ? undefined : isSun ? PRINT_SUN : dow === 6 ? PRINT_SAT : undefined;
              return (
                <th key={d.date} data-day={true} style={{ background: mono ? undefined : dow === 0 || d.publicHolidayName ? '#FBEAEA' : dow === 6 ? '#EAF2FB' : undefined, color: fg }}>
                  {/* 日付の数字は折らずに、列に入る大きさで出す（10/5。以前は 10〜31 が 1 桁ずつ 2 段に折れていた） */}
                  <div style={{ whiteSpace: 'nowrap', fontSize: `${dateFs}px` }}>{parseDateStr(d.date).getDate()}</div>
                  <div style={{ fontSize: `${Math.min(fs - 2, dateFs)}px` }}>
                    {DOW_LABELS[dow]}
                    {d.isStoreHoliday ? ' 休' : ''}
                  </div>
                </th>
              );
            })}
                {showTotals && <th>合計</th>}
              </tr>
            </thead>
            <tbody>
              {g.staffs.map((st, si) => (
                <tr key={st.staffCode} data-stripe={si % 2 === 1}>
                  {/* 名前と合計は行の高さの中央に置く。セルの時刻は上から詰める */}
                  <td style={{ verticalAlign: 'middle' }}>
                    <div style={{ fontWeight: 600, fontSize: `${Math.round(fs * (setting.staffNameScale / 100))}px` }}>{st.staffName}</div>
                    {st.isGuest && <div style={{ fontSize: `${fs - 2}px` }}>{`支援 / ${st.belongOrgName}`}</div>}
                    {hasItem(setting, 'business') && st.mainBusinessName && <div style={{ fontSize: `${fs - 2}px` }}>{st.mainBusinessName}</div>}
                  </td>
                  {board.days.map((d) => {
                const k = `${st.staffCode}|${d.date}`;
                const list = byCell[k] || [];
                return (
                  <td key={d.date} data-day={true} style={{ background: d.isStoreHoliday && !mono ? '#F0F0F0' : undefined }}>
                    {hasItem(setting, 'assignment') && list.map((s) => {
                      // 公休は盤面のカードと同じ見た目（10/7 案 B）。法定は白黒の印刷でも濃い塗りで出す（区別しない人の公休は「公休」だけ）
                      const hol = s.attendType !== 0 ? holidayLook(s.isLegal, st.distinctionHoliday) : null;
                      const legalFill = !!hol && hol.kind === '法定';
                      const bg = hol ? (mono ? (legalFill ? '#555' : '#fff') : hol.bg) : (mono ? '#fff' : (s.color || defaultPatternColor(s.shiftPatternId)));
                      const label = hol ? hol.short : (s.shiftPatternShortName || '');
                      const extra = `${hasItem(setting, 'break') && s.breakMinutes > 0 ? `(休${s.breakMinutes})` : ''}`
                        + `${hasItem(setting, 'supportOrg') && s.isAway ? `[${s.awayOrgName || '支援'}]` : ''}`
                        + `${hasItem(setting, 'business') && s.mainBusinessName && s.attendType === 0 ? ` ${s.mainBusinessName}` : ''}`;
                      const chipStyle = {
                        background: bg,
                        color: hol ? (legalFill ? '#fff' : (mono ? '#111' : hol.fg)) : (mono ? '#111' : textColorFor(bg)),
                        border: mono ? '1px solid #333' : undefined,
                      };
                      const hasTime = s.attendType === 0 && !!s.start;
                      const end = `${s.end || ''}${s.isEndNextDay ? '翌' : ''}`;
                      const timeStyle = { fontSize: `${timeFs}px`, fontWeight: 700, lineHeight: 1.15 };
                      return (
                        <span key={s.shiftId} css={narrow ? styles.chipNarrow : styles.chip} style={chipStyle}>
                          {/* 公休は時刻がないので、略称を時刻と同じ扱いで出す。略称・公休はセルの中で折り返す（切らない。10/5） */}
                          {!!label && (hasTime
                            ? <span css={styles.chipWrap} style={{ fontSize: `${labelFs}px`, lineHeight: 1.2 }}>{label}</span>
                            : <span css={styles.chipWrap} style={{ ...timeStyle, fontSize: `${Math.min(timeFs, fs)}px` }}>{label}</span>)}
                          {hasTime && narrow && (
                            <React.Fragment>
                              <span css={styles.chipLine} style={timeStyle}>{s.start}</span>
                              <span css={styles.chipLine} style={timeStyle}>{end}</span>
                            </React.Fragment>
                          )}
                          {hasTime && !narrow && <span css={styles.chipLine} style={timeStyle}>{`${s.start}-${end}`}</span>}
                          {!!extra && <span css={styles.chipWrap} style={{ fontSize: `${labelFs}px` }}>{extra}</span>}
                        </span>
                      );
                    })}
                    {hopes[k] && <div style={{ fontSize: `${fs - 2}px` }}>{hopes[k]}</div>}
                  </td>
                );
              })}
                  {showTotals && (
                  <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                    {byStaff[st.staffCode] ? `${byStaff[st.staffCode].workDays}日 ${formatTime(byStaff[st.staffCode].workMinutes)}` : ''}
                    {showLabor && byStaff[st.staffCode] && byStaff[st.staffCode].laborCost !== null && <div style={{ fontSize: `${fs - 2}px` }} title={`人件費（${LABOR_COST_TIP}）`}>{`¥${(laborOnly(byStaff[st.staffCode].laborCost, byStaff[st.staffCode].transportation) as number).toLocaleString()}`}</div>}
                  </td>
              )}
                </tr>
          ))}
            </tbody>
            {hasItem(setting, 'headcount') && (
            <tfoot>
              <tr>
                <th style={{ textAlign: 'left' }}>人数 / 予定</th>
                {board.days.map((d) => {
                const r = required[d.date];
                // 数字は折らずに列に入る大きさで出し、「/」のあとでだけ折り返す（切ると「8/10」が「8/1」に見えるため。10/5）
                return (
                  <th key={d.date} data-day={true} style={{ fontSize: `${dateFs}px` }}>
                    {r && r.required !== null ? (
                      <React.Fragment>
                        {`${r.assigned}/`}
                        <wbr />
                        {r.required}
                      </React.Fragment>
                    ) : (r ? r.assigned : '')}
                  </th>
                );
              })}
                {showTotals && <th />}
              </tr>
            </tfoot>
        )}
          </table>
        </div>
      ))}
      {hasItem(setting, 'patternLegend') && board.shiftPatterns.length > 0 && (
        <div css={styles.legend} style={{ fontSize: `${fs}px`, gridTemplateColumns: `repeat(auto-fill, minmax(${fs * 17}px, 1fr))` }}>
          {board.shiftPatterns.map((p) => {
            const bg = mono ? '#fff' : (p.color || defaultPatternColor(p.shiftPatternId));
            const l = legendLabel(p);
            // セルと同じ配分にする（9/24）。時刻が主役で太く、略称と名前は添え物として小さく
            return (
              <span key={p.shiftPatternId} css={styles.legendItem}>
                <span
                  css={styles.legendSwatch}
                  style={{
 background: bg, color: mono ? '#111' : textColorFor(bg), fontSize: `${labelFs}px`, fontWeight: 600,
}}
                >
                  {p.shortName}
                </span>
                <span css={styles.legendText}>
                  {!!l.time && <span style={{ fontWeight: 700 }}>{l.time}</span>}
                  {!!l.name && <span style={{ fontSize: `${labelFs}px`, color: mono ? '#111' : '#555' }}>{`${l.time ? ' ' : ''}${l.name}`}</span>}
                </span>
              </span>
            );
          })}
          {board.org.distinctionHoliday ? (
            <React.Fragment>
              <span css={styles.legendItem}>
                <span
                  css={styles.legendSwatch}
                  style={{
 background: mono ? '#555' : holidayLook(true, true).bg, color: '#fff', fontSize: `${labelFs}px`, fontWeight: 600,
}}
                >
                  公休(法)
                </span>
                <span css={styles.legendText}>法定休日</span>
              </span>
              <span css={styles.legendItem}>
                <span css={styles.legendSwatch} style={{ background: mono ? '#fff' : holidayLook(false, true).bg, fontSize: `${labelFs}px`, fontWeight: 600 }}>公休(所)</span>
                <span css={styles.legendText}>所定休日</span>
              </span>
            </React.Fragment>
          ) : (
            <span css={styles.legendItem}>
              <span css={styles.legendSwatch} style={{ background: '#fff', fontSize: `${labelFs}px`, fontWeight: 600 }}>公休</span>
              <span css={styles.legendText}>休日</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PrintSheet;
